/**
 * Cart-drawer cross-sell: Rebuy custom data source → Stretch cards.
 * API params mirror Rebuy Smart Cart (product_groups, cart[…] brackets, post-discount subtotal).
 */
(function () {
  if (typeof window.customElements === 'undefined' || !Shopify.routes?.root) return;

  const REBUY_HOST = 'https://rebuyengine.com';

  /** @type {{ key: string | null, products: any[] | null, inflight: Promise<any[]> | null, inflightKey: string | null }} */
  const recommendationCache = {
    key: null,
    products: null,
    inflight: null,
    inflightKey: null,
  };

  /** @type {any | null} */
  let lastCartJson = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let fetchDebounceTimer = null;

  /** @type {AbortController | null} */
  let rebuyAbort = null;

  function escapeHtml(text) {
    const d = document.createElement('div');
    d.textContent = text ?? '';
    return d.innerHTML;
  }

  function escapeAttr(val) {
    return String(val ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  /** Shopify ajax `/products/[handle].js` variant prices usually decimal dollars; cart.js uses cents. */
  function priceToCents(raw) {
    if (raw === null || raw === undefined || raw === false || raw === '') return 0;
    if (typeof raw === 'number') {
      if (Number.isInteger(raw)) return raw;
      return Math.round(raw * 100);
    }
    const n = parseFloat(String(raw ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  function compareAtPriceToCentsOrNull(raw) {
    if (raw === null || raw === undefined || raw === false || raw === '') return null;
    const c = priceToCents(raw);
    return c > 0 ? c : null;
  }

  /**
   * Port of snippets/fragrance-title.liquid (variant → tag → handle, title stripping).
   * Keep handle rules in same elsif order as Liquid.
   */
  const FRAGRANCE_HANDLE_RULES = [
    { needle: 'objets-damsterdam', label: "Objets d'Amsterdam", labelAlt: 'Objets d\u2019Amsterdam', number: '12' },
    { needle: 'violet-muse', label: 'Violet Muse', number: '11' },
    { needle: 'lemon-notes', label: 'Lemon Notes', number: '09' },
    { needle: 'voyage-vetiver', label: 'Voyage Vetiver', labelAlt: 'Voyage Vétiver', number: '07' },
    { needle: 'nuit-des-figues', label: 'Nuit des Figues', number: '08' },
    { needle: 'rock-roses', label: 'Rock Roses', number: '10' },
    { needle: 'poivre-noir-frais', label: 'Poivre Noir Frais', number: '03' },
    { needle: 'lheure-santal', label: "L'Heure Santal", labelAlt: 'L\u2019Heure Santal', number: '13' },
    { needle: 'firewood-green', label: 'Firewood Green', number: '14' },
    { needle: 'rosemarys-touch', label: "Rosemary's Touch", labelAlt: 'Rosemary\u2019s Touch', number: '75' },
    { needle: 'amber-haze', label: 'Amber Haze', number: '15' },
    { needle: 'bergamot-leaf', label: 'Bergamot Leaf', number: '05' },
    { needle: 'flower-water', label: 'Flower Water', number: '20' },
    { needle: 'rose-fumee', label: 'Rose Fumée', labelAlt: 'Rose Fumee', number: '17' },
    { needle: 'terre-fertile', label: 'Terre Fertile', number: '18' },
    { needle: 'suede-spice', label: 'Suede Spice', number: '19' },
  ];

  function normalizeProductTags(tags) {
    if (Array.isArray(tags)) return tags.map((t) => String(t).trim()).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map((s) => s.trim()).filter(Boolean);
    return [];
  }

  function tagsInclude(tags, slug) {
    const want = slug.toLowerCase();
    return normalizeProductTags(tags).some((t) => t.toLowerCase() === want);
  }

  function collapseSpaces(s) {
    return String(s ?? '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function matchFragranceHandleRule(handle) {
    const h = String(handle || '').toLowerCase();
    for (let i = 0; i < FRAGRANCE_HANDLE_RULES.length; i++) {
      const r = FRAGRANCE_HANDLE_RULES[i];
      if (h.includes(r.needle)) return r;
    }
    return null;
  }

  function tokenLooksLikeSize(token) {
    const down = token.toLowerCase();
    const withoutMl = down.replace(/ml/g, '');
    if (withoutMl !== down && Number(withoutMl) > 0) return true;
    const withoutG = down.replace(/g/g, '');
    if (withoutG !== down && Number(withoutG) > 0) return true;
    return token === 'L' || token === 'l';
  }

  function getVariantSizeOption(product, variant) {
    const opts = product?.options;
    if (!Array.isArray(opts) || !variant) return '';
    let sizeIdx = -1;
    for (let i = 0; i < opts.length; i++) {
      if (String(opts[i]).toLowerCase() === 'size') sizeIdx = i;
    }
    if (sizeIdx === 0) return String(variant.option1 ?? '').trim();
    if (sizeIdx === 1) return String(variant.option2 ?? '').trim();
    if (sizeIdx === 2) return String(variant.option3 ?? '').trim();
    return '';
  }

  function deaccentLabel(label) {
    return label
      .replace(/é/g, 'e')
      .replace(/è/g, 'e')
      .replace(/ê/g, 'e')
      .replace(/ë/g, 'e')
      .replace(/à/g, 'a')
      .replace(/â/g, 'a')
      .replace(/ù/g, 'u')
      .replace(/û/g, 'u')
      .replace(/ô/g, 'o')
      .replace(/î/g, 'i')
      .replace(/ï/g, 'i')
      .replace(/ç/g, 'c')
      .replace(/É/g, 'E')
      .replace(/È/g, 'E')
      .replace(/Ê/g, 'E')
      .replace(/À/g, 'A')
      .replace(/Â/g, 'A')
      .replace(/Ù/g, 'U')
      .replace(/Ô/g, 'O')
      .replace(/Î/g, 'I')
      .replace(/Ç/g, 'C');
  }

  function buildLabelCandidates(fragranceLabel, fragranceLabelAlt) {
    if (!fragranceLabel) return [];
    const seen = new Set();
    const out = [];
    const push = (s) => {
      const t = String(s ?? '').trim();
      if (!t || seen.has(t)) return;
      seen.add(t);
      out.push(t);
    };

    push(fragranceLabel);
    if (fragranceLabelAlt) push(fragranceLabelAlt);
    push(fragranceLabel.replace(/\u2019/g, "'"));
    push(fragranceLabel.replace(/'/g, '\u2019'));
    const deacc = deaccentLabel(fragranceLabel);
    if (deacc !== fragranceLabel) push(deacc);
    const accented = fragranceLabel.replace(/Vetiver/g, 'Vétiver').replace(/Fumee/g, 'Fumée');
    if (accented !== fragranceLabel) push(accented);
    const plain = fragranceLabel.replace(/Vétiver/g, 'Vetiver').replace(/Fumée/g, 'Fumee');
    if (plain !== fragranceLabel) push(plain);
    return out;
  }

  function stripFromTitle(titleMain, needle) {
    if (!needle) return titleMain;
    if (titleMain.includes(needle)) {
      return collapseSpaces(titleMain.split(needle).join(''));
    }
    const parts = titleMain.split(needle);
    if (parts.length > 1) return collapseSpaces(parts[0]);
    return titleMain;
  }

  function buildTitleMain(productTitle, fragranceLabel, fragranceLabelAlt, fragranceNumber, fragranceSubtitle, sizeToStrip) {
    let titleMain = String(productTitle ?? '').trim();
    if (!fragranceLabel) return collapseSpaces(titleMain);

    const candidates = buildLabelCandidates(fragranceLabel, fragranceLabelAlt);
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      const next = stripFromTitle(titleMain, c);
      if (next !== titleMain) {
        titleMain = next;
        break;
      }
    }

    if (fragranceSubtitle) {
      titleMain = stripFromTitle(titleMain, fragranceSubtitle);
      titleMain = stripFromTitle(titleMain, fragranceSubtitle.replace(/\u2019/g, "'"));
      titleMain = stripFromTitle(titleMain, fragranceSubtitle.replace(/'/g, '\u2019'));
    }

    if (fragranceNumber) {
      for (let i = 0; i < candidates.length; i++) {
        const prefixed = `No.${fragranceNumber} ${candidates[i]}`;
        const next = stripFromTitle(titleMain, prefixed);
        if (next !== titleMain) {
          titleMain = next;
          break;
        }
      }
    }

    if (sizeToStrip) {
      titleMain = stripFromTitle(titleMain, sizeToStrip);
      const compact = sizeToStrip.replace(/\s/g, '');
      if (compact !== sizeToStrip) titleMain = stripFromTitle(titleMain, compact);
      const sizeDown = sizeToStrip.toLowerCase();
      const sizeNum = sizeDown.replace(/ml/g, '').replace(/g/g, '').trim();
      if (sizeNum) {
        titleMain = stripFromTitle(titleMain, `${sizeNum} ml`);
        titleMain = stripFromTitle(titleMain, sizeNum);
      }
    }

    return collapseSpaces(titleMain);
  }

  /**
   * @returns {{ titleMain: string, fragranceSubtitle: string }}
   */
  function resolveFragranceDisplay(product, variant) {
    let fragranceSubtitle = '';
    let fragranceLabel = '';
    let fragranceLabelAlt = '';
    let fragranceNumber = '';
    let fragranceSource = '';
    let sizeToStrip = '';

    const variantTitle = String(variant?.title ?? '').trim();
    if (variantTitle && variantTitle !== 'Default Title') {
      fragranceSubtitle = variantTitle;
      fragranceSource = 'variant';
    }

    if (!fragranceSubtitle) {
      const tags = normalizeProductTags(product?.tags);
      for (let i = 0; i < tags.length; i++) {
        const tag = tags[i];
        if (tag.slice(0, 3) === 'No.') {
          fragranceSubtitle = tag;
          fragranceSource = 'tag';
          break;
        }
      }
    }

    if (!fragranceSubtitle) {
      const rule = matchFragranceHandleRule(product?.handle);
      if (rule) {
        fragranceLabel = rule.label;
        fragranceLabelAlt = rule.labelAlt || '';
        fragranceNumber = rule.number;
        fragranceSubtitle = `No.${fragranceNumber} ${fragranceLabel}`;
        fragranceSource = 'handle';
      }
    }

    if (fragranceSubtitle && !fragranceLabel) {
      let fragranceLine = fragranceSubtitle;
      let fragranceNoLine = fragranceLine;
      const slashIdx = fragranceLine.indexOf('/');
      if (slashIdx !== -1) {
        fragranceNoLine = fragranceLine.slice(0, slashIdx).trim();
        sizeToStrip = fragranceLine.slice(slashIdx + 1).trim();
      }

      const afterNo = fragranceNoLine.split('No.').pop()?.trim() || '';
      const tokens = afterNo.split(/\s+/).filter(Boolean);
      if (tokens.length) fragranceNumber = tokens[0];

      for (let t = 1; t < tokens.length; t++) {
        const token = tokens[t];
        if (tokenLooksLikeSize(token)) {
          sizeToStrip = token;
        } else if (!fragranceLabel) {
          fragranceLabel = token;
        } else {
          fragranceLabel = `${fragranceLabel} ${token}`;
        }
      }
    }

    if (fragranceLabel && !fragranceLabelAlt) {
      const rule = matchFragranceHandleRule(product?.handle);
      if (rule?.labelAlt) fragranceLabelAlt = rule.labelAlt;
    }

    if (fragranceSource === 'variant' && !sizeToStrip) {
      sizeToStrip = getVariantSizeOption(product, variant);
    }

    const titleMain = buildTitleMain(
      product?.title,
      fragranceLabel,
      fragranceLabelAlt,
      fragranceNumber,
      fragranceSubtitle,
      sizeToStrip
    );

    return { titleMain, fragranceSubtitle };
  }

  function buildFragranceTitleHtml(product, productUrl, atVariant, titleClassProp) {
    const tc = escapeAttr(((titleClassProp ?? '') || 'subheading').trim() || 'subheading');
    const titleClasses = `product-title ${tc}`.trim();
    const title = (product.title || '').trim();
    const plain = `<a href="${escapeAttr(productUrl)}" class="${titleClasses}" data-instant>${escapeHtml(title)}</a>`;

    if (tagsInclude(product.tags, 'no_subtitle')) return plain;

    const { titleMain, fragranceSubtitle } = resolveFragranceDisplay(product, atVariant);
    let subtitle = fragranceSubtitle;
    if (!subtitle) {
      const opt1 = atVariant?.option1 != null ? String(atVariant.option1).trim() : '';
      if (opt1 && opt1 !== 'Default Title') subtitle = opt1;
    }

    let html = `<a href="${escapeAttr(productUrl)}" class="${titleClasses}" data-instant>${escapeHtml(titleMain || title)}</a>`;
    if (subtitle) html += `<span class="fragrance-subtitle text-subdued">${escapeHtml(subtitle)}</span>`;
    return html;
  }

  function formatMoney(cents, currencyCodeEnabled, currencyIso) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
    const fmt = window.themeVariables?.settings?.moneyFormat ?? '{{amount}}';
    const value = (cents / 100).toFixed(2).replace(/\.00$/, '');
    let out = fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount\s*\}\}/g, value);
    if (currencyCodeEnabled && currencyIso) out = `${out} ${currencyIso}`;
    return out;
  }

  /** @param {any} cart */
  function rebuyCartLevelDiscountCents(cart) {
    return (cart?.cart_level_discount_applications || []).reduce(
      (sum, discount) => sum + (discount.total_allocated_amount || 0),
      0
    );
  }

  /** Mirrors gwp-manager.js cartSubtotal() — shipped qualifying lines minus cart-level discounts. */
  function rebuyQualifyingSubtotalCents(cart) {
    if (!cart?.items?.length) return 0;

    let total = 0;
    for (const item of cart.items) {
      if (item.requires_shipping && item.properties?._gwp !== 'true') {
        total += Number(item.final_line_price ?? 0);
      }
    }

    const discounted = Math.max(0, total - rebuyCartLevelDiscountCents(cart));
    if (discounted > 0) return discounted;

    const itemsSubtotal = Math.max(
      0,
      Number(cart.items_subtotal_price || 0) - rebuyCartLevelDiscountCents(cart)
    );
    if (itemsSubtotal > 0) return itemsSubtotal;

    return Math.max(0, Number(cart.total_price || 0));
  }

  /** Lines that drive cross-sell rules — exclude gifts and free Rebuy promos. */
  function merchandisingLines(cart) {
    if (!Array.isArray(cart?.items)) return [];

    return cart.items.filter((item) => {
      if (item.properties?._early_access === 'true') return true;
      if (item.properties?._gwp === 'true') return false;
      if (item.final_line_price !== 0) return true;
      const props = item.properties || {};
      if (props._source === 'Rebuy' || props._widget_id || props._attribution) return false;
      return true;
    });
  }

  /** Rebuy band rules compare cart_subtotal in Shopify cents (3500 = €35). */
  function rebuySubtotalParam(cents) {
    return String(Math.max(0, Math.round(Number(cents) || 0)));
  }

  function rebuySubtotalEuros(cents) {
    const value = (Number(cents) || 0) / 100;
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  /** @param {any} cart @param {number} subtotalCents */
  function rebuyCartAttributesPayload(cart, subtotalCents) {
    const attrs = { ...(cart?.attributes || {}) };
    const subtotal = subtotalCents / 100;

    if (subtotalCents > 0) {
      attrs._gwp_post_discount_qualifying = rebuySubtotalEuros(subtotalCents);
    }

    if (!attrs._source) attrs._source = 'MSM Theme Cart';
    if (!attrs._attribution) attrs._attribution = 'Cart Drawer Cross-sell';

    return attrs;
  }

  /**
   * Query shape aligned with Rebuy Smart Cart (datasource 185599).
   * @param {URLSearchParams} params
   * @param {any} cart
   * @param {Record<string, unknown>} extra
   * @param {{ subtotalCents?: number }} [options]
   */
  function appendRebuyQueryParams(params, cart, extra, options = {}) {
    Object.entries(extra).forEach(([k, v]) => {
      if (v == null || v === '') return;
      params.append(k, String(v));
    });

    params.append('url', window.location.href);
    params.append('product_groups', 'yes');
    params.append('cache_key', cartSignature(cart));
    params.append('shopify_collection_ids', '');
    params.append('shopify_order_ids', '');

    const items = merchandisingLines(cart);
    const itemCount = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const lineCount = items.length;
    const subtotalCents = options.subtotalCents ?? rebuyQualifyingSubtotalCents(cart);
    const subtotal = rebuySubtotalParam(subtotalCents);
    const token = cart?.token || '';

    const productIds = items.map((item) => item.product_id).filter(Boolean);
    const variantIds = items.map((item) => item.variant_id).filter(Boolean);
    params.append('shopify_product_ids', productIds.join(','));
    params.append('shopify_variant_ids', variantIds.join(','));

    if (token) params.append('cart_token', token);
    params.append('cart_subtotal', subtotal);
    params.append('cart_count', String(itemCount));
    params.append('cart_line_count', String(lineCount));
    params.append('cart_item_count', String(itemCount));

    if (cart?.total_price != null) {
      params.append('cart_total', String(cart.total_price / 100));
    }

    if (token) params.append('cart[token]', token);
    params.append('cart[subtotal]', subtotal);
    params.append('cart[line_count]', String(lineCount));
    params.append('cart[item_count]', String(itemCount));
    params.append('cart[notes]', cart?.note || '');
    params.append('cart[attributes]', JSON.stringify(rebuyCartAttributesPayload(cart, subtotalCents)));

    items.forEach((item, index) => {
      if (item?.product_id) params.append(`cart[items][${index}][product_id]`, String(item.product_id));
      if (item?.variant_id) params.append(`cart[items][${index}][variant_id]`, String(item.variant_id));
      if (item?.quantity != null) params.append(`cart[items][${index}][quantity]`, String(item.quantity));
      if (item?.key) params.append(`cart[items][${index}][key]`, String(item.key));
      if (item?.price != null) params.append(`cart[items][${index}][price]`, rebuySubtotalParam(Number(item.price)));
      if (item?.line_price != null) {
        params.append(`cart[items][${index}][line_price]`, rebuySubtotalParam(Number(item.line_price)));
      }
      if (item?.final_line_price != null) {
        params.append(`cart[items][${index}][final_line_price]`, rebuySubtotalParam(Number(item.final_line_price)));
      }
      if (item?.final_price != null) {
        params.append(`cart[items][${index}][final_price]`, rebuySubtotalParam(Number(item.final_price)));
      }
    });

    if (cart?.currency) params.append('currency', cart.currency);
  }

  function cartSignature(cart) {
    const lines = merchandisingLines(cart);
    if (!lines.length) return 'empty';

    const items = lines
      .map((item) => `${item.product_id}:${item.variant_id}:${item.quantity}`)
      .sort()
      .join('|');
    const subtotal = rebuySubtotalParam(rebuyQualifyingSubtotalCents(cart));
    const discounts = (cart.cart_level_discount_applications || [])
      .map((discount) => `${discount.title || ''}:${discount.total_allocated_amount || 0}`)
      .sort()
      .join('|');

    return `${items}|sub:${subtotal}|disc:${discounts}`;
  }

  function inCartProductIds(cart) {
    const set = new Set();
    cart?.items?.forEach((line) => {
      if (line?.product_id) set.add(line.product_id);
    });
    return set;
  }

  function clearRecommendationCache() {
    recommendationCache.key = null;
    recommendationCache.products = null;
    recommendationCache.inflight = null;
    recommendationCache.inflightKey = null;
  }

  async function loadCartJson() {
    const res = await fetch(`${Shopify.routes.root}cart.js`, { cache: 'no-store' });
    return res.ok ? await res.json() : null;
  }

  function cartIsApiReady(cart) {
    return (
      Array.isArray(cart?.items) &&
      merchandisingLines(cart).length > 0 &&
      cart.items_subtotal_price != null
    );
  }

  async function resolveCartForApi(cartHint) {
    if (cartIsApiReady(cartHint)) return cartHint;
    const fresh = await loadCartJson();
    return fresh || cartHint;
  }

  function abortRebuyFetch() {
    if (rebuyAbort) {
      rebuyAbort.abort();
      rebuyAbort = null;
    }
  }

  /**
   * @param {{ subtotalCents?: number }} [options]
   * @returns {Promise<{ data: any[], metadata: Record<string, unknown> | null }>}
   */
  async function fetchDataSource(apiKey, dataSourceId, cart, extra, limit, options = {}, signal) {
    const params = new URLSearchParams();
    params.append('key', apiKey);
    params.append('limit', String(limit));
    appendRebuyQueryParams(params, cart, extra, options);
    const url = `${REBUY_HOST}/api/v1/custom/id/${encodeURIComponent(String(dataSourceId))}?${params}`;

    const res = await fetch(url, { method: 'GET', credentials: 'omit', cache: 'no-store', signal });
    if (!res.ok) throw new Error(`Rebuy ${res.status}`);
    const json = await res.json();
    return { data: json.data || [], metadata: json.metadata || null };
  }

  function hideShopifyFallback(sectionId) {
    const node = document.getElementById(`cart-drawer-cross-sell-fallback-${sectionId}`);
    if (!node) return;
    node.hidden = true;
    node.removeAttribute('data-active');
    node.replaceChildren();
  }

  function showShopifyFallback(sectionId) {
    const node = document.getElementById(`cart-drawer-cross-sell-fallback-${sectionId}`);
    if (!node || node.dataset.active === 'true') return;

    const seed = node.dataset.fallbackSeed;
    if (!seed) return;

    node.dataset.active = 'true';
    node.hidden = false;

    const pr = document.createElement('product-recommendations');
    pr.setAttribute('product', seed);
    pr.setAttribute('limit', '10');
    pr.setAttribute('intent', 'complementary');
    pr.setAttribute('section-id', node.dataset.sectionId || sectionId);
    node.appendChild(pr);
  }

  /** @param {any} raw */
  function normalizeRebuyProduct(raw) {
    if (!raw?.id || !raw?.handle) return null;

    let featured_image = '';
    if (typeof raw.featured_image === 'string') featured_image = raw.featured_image;
    else if (raw.image?.src) featured_image = raw.image.src;
    else if (raw.images?.[0]?.src) featured_image = raw.images[0].src;

    const variants = (raw.variants ?? []).map((variant) => ({
      ...variant,
      available: variant?.available !== false && variant?.available !== 'false',
    }));

    if (!variants.length) return null;

    const prices = new Set(variants.map((variant) => String(variant.price ?? '')));

    return {
      id: raw.id,
      handle: raw.handle,
      title: raw.title,
      tags: raw.tags,
      template_suffix: raw.template_suffix,
      available: raw.available !== false && raw.available !== 'false',
      variants,
      featured_image,
      images: raw.images,
      selling_plan_groups: raw.selling_plan_groups ?? [],
      options: raw.options,
      price_varies: variants.length > 1 && prices.size > 1,
    };
  }

  /** @param {any[]} rebuyProducts @param {Set<number>} inCart */
  function productsFromRebuy(rebuyProducts, inCart, limit) {
    const orderedProducts = [];
    const seenIds = new Set();

    rebuyProducts.forEach((raw) => {
      if (orderedProducts.length >= limit) return;

      const product = normalizeRebuyProduct(raw);
      if (!product || seenIds.has(product.id)) return;

      seenIds.add(product.id);
      if (inCart.has(product.id)) return;
      if (product.template_suffix === 'gwp') return;

      orderedProducts.push(product);
    });

    return orderedProducts;
  }

  /**
   * @param {Record<string, unknown>} d
   * @returns {Promise<any[]>}
   */
  async function fetchRecommendations(cartHint, apiKey, dataSourceId, d, limit) {
    const hintKey = cartHint ? cartSignature(cartHint) : null;

    if (hintKey && recommendationCache.key === hintKey && recommendationCache.products) {
      return recommendationCache.products;
    }

    if (recommendationCache.inflight) {
      if (!hintKey || recommendationCache.inflightKey === hintKey) {
        return recommendationCache.inflight;
      }
      abortRebuyFetch();
      recommendationCache.inflight = null;
      recommendationCache.inflightKey = null;
    }

    const abort = new AbortController();
    rebuyAbort = abort;
    recommendationCache.inflightKey = hintKey;
    recommendationCache.inflight = (async () => {
      try {
        const cart = await resolveCartForApi(cartHint);
        if (!merchandisingLines(cart).length) return [];

        lastCartJson = cart;
        const activeKey = cartSignature(cart);

        if (recommendationCache.key === activeKey && recommendationCache.products) {
          return recommendationCache.products;
        }

        const inCart = inCartProductIds(cart);
        const extra = {};
        if (d.countryCode) extra.country_code = d.countryCode;

        const subtotalCents = rebuyQualifyingSubtotalCents(cart);
        const rebuyResult = await fetchDataSource(
          apiKey,
          dataSourceId,
          cart,
          extra,
          limit,
          { subtotalCents },
          abort.signal
        );

        const orderedProducts = productsFromRebuy(rebuyResult.data, inCart, limit);

        if (orderedProducts.length) {
          recommendationCache.key = activeKey;
          recommendationCache.products = orderedProducts;
        }

        return orderedProducts;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        throw error;
      } finally {
        if (rebuyAbort === abort) rebuyAbort = null;
        if (recommendationCache.inflightKey === hintKey) {
          recommendationCache.inflight = null;
          recommendationCache.inflightKey = null;
        }
      }
    })();

    return recommendationCache.inflight;
  }

  function pickDisplayVariant(product, priceStrategy) {
    const list = [...(product.variants ?? [])].sort(
      (a, b) => priceToCents(a.price) - priceToCents(b.price)
    );
    if (!list.length) return null;
    if (product.price_varies && priceStrategy !== 'from_price') return list[list.length - 1];
    return list[0];
  }

  function firstAvailableVariant(product) {
    const variants = product.variants ?? [];
    const found =
      variants.find((x) => x.available === true) ??
      variants.find((x) => x.available !== false) ??
      null;
    return found || variants[0] || null;
  }

  /** @typedef {{ currencyCodeEnabled: boolean, currencyIso: string, productCardPriceStrategy: string, fromPriceTemplate: string, regularPriceClasses: string, onSalePriceClasses: string, salePriceClasses: string, comparePriceClasses: string }} Ds */

  /** @param {Ds} datasets */
  function buildPriceMarkup(product, displayVariant, datasets) {
    const strategy = datasets.productCardPriceStrategy || 'first_available';
    const cur = datasets.currencyIso || '';
    const codeOn = Boolean(datasets.currencyCodeEnabled);

    let regClass = datasets.regularPriceClasses || '';
    let cmpClass = datasets.comparePriceClasses || '';

    if (product.available === false || !displayVariant) {
      return `<price-list class="price-list"><sale-price class="${escapeAttr(regClass)}"></sale-price></price-list>`;
    }

    const dPrice = priceToCents(displayVariant.price);
    const cmpCents = compareAtPriceToCentsOrNull(displayVariant.compare_at_price);
    const onSale = cmpCents != null && cmpCents > dPrice;
    const compareValue = onSale ? cmpCents : null;

    let saleClass = onSale ? (datasets.onSalePriceClasses || datasets.salePriceClasses || regClass) : regClass;

    let saleInner = '';

    if (product.price_varies && strategy === 'from_price' && datasets.fromPriceTemplate) {
      const tmpl = datasets.fromPriceTemplate;
      const ph = datasets.fromPricePlaceholder || '__MIN__';
      const minVariant = [...(product.variants ?? [])].sort(
        (a, b) => priceToCents(a.price) - priceToCents(b.price)
      )[0];
      const minStr = formatMoney(priceToCents(minVariant?.price ?? displayVariant.price), codeOn, cur);
      const label = tmpl.split(ph).join(minStr);

      saleClass = onSale ? (datasets.onSalePriceClasses || datasets.salePriceClasses || regClass) : regClass;
      saleInner = `<span class="sr-only"></span>${label}`;
    } else {
      saleInner = `<span class="sr-only"></span>${formatMoney(dPrice, codeOn, cur)}`;
    }

    let compare = '';
    if (!(product.price_varies && strategy === 'from_price') && compareValue != null) {
      const cmpMerged = /\btext-subdued\b/.test(cmpClass) ? cmpClass : `text-subdued line-through ${cmpClass}`.trim();
      compare = `<compare-at-price class="${escapeAttr(cmpMerged)}"><span class="sr-only"></span>${formatMoney(
        compareValue,
        codeOn,
        cur
      )}</compare-at-price>`;
    }

    return `<price-list class="price-list"><sale-price class="${escapeAttr(saleClass)}">${saleInner}</sale-price>${compare}</price-list>`;
  }

  /**
   * JSON config in a sibling <script type="application/json" id="…"> (required: <template> parses `</…>` in text as real tags and can leak JSON into the DOM).
   * @returns {Ds & Record<string, unknown>}
   */
  function readConfig(el) {
    const id = el?.getAttribute?.('data-config-id');
    if (!id) return {};
    const node = document.getElementById(id);
    try {
      if (!node) return {};
      const raw = (node.textContent || '').trim();
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  /**
   * API credentials from the live element when present, else themeVariables (empty-cart prefetch).
   * @returns {{ apiKey: string, dataSourceId: string, limit: number, config: Record<string, unknown> } | null}
   */
  function getRebuySource() {
    const el = document.querySelector('rebuy-cart-cross-sell');
    if (el) {
      const apiKey = (el.getAttribute('api-key') || '').trim();
      const dataSourceId = (el.getAttribute('datasource-id') || '').trim();
      if (apiKey && dataSourceId) {
        return {
          apiKey,
          dataSourceId,
          limit: Number(el.getAttribute('limit')) || 10,
          config: readConfig(el),
        };
      }
    }

    const global = window.themeVariables?.rebuyCrossSell;
    if (global?.apiKey && global?.dataSourceId) {
      return {
        apiKey: String(global.apiKey).trim(),
        dataSourceId: String(global.dataSourceId).trim(),
        limit: Number(global.limit) || 10,
        config: { countryCode: global.countryCode ?? '' },
      };
    }

    return null;
  }

  function scheduleRecommendationFetch(cart, delayMs = 0) {
    clearTimeout(fetchDebounceTimer);
    if (delayMs <= 0) {
      runRecommendationFetch(cart).catch(() => {});
      return;
    }

    fetchDebounceTimer = setTimeout(() => {
      fetchDebounceTimer = null;
      runRecommendationFetch(cart || lastCartJson).catch(() => {});
    }, delayMs);
  }

  async function runRecommendationFetch(cartHint) {
    const source = getRebuySource();
    if (!source) return;

    const cart = cartHint || lastCartJson;
    if (!merchandisingLines(cart).length) {
      clearRecommendationCache();
      return;
    }

    lastCartJson = cart;

    try {
      await fetchRecommendations(cart, source.apiKey, source.dataSourceId, source.config, source.limit);
      paintCrossSellFromCache();
    } catch (error) {
      if (error?.name !== 'AbortError') throw error;
    }
  }

  function filterCachedProducts(cart) {
    if (!recommendationCache.products?.length) return [];

    const inCart = inCartProductIds(cart);
    return recommendationCache.products.filter((product) => !inCart.has(product.id));
  }

  /** Show last-known recs while a new cart signature loads (stale-while-revalidate). */
  function paintCrossSellStale(cart) {
    const sig = cartSignature(cart);
    if (recommendationCache.key === sig && recommendationCache.products?.length) {
      paintCrossSellFromCache();
      return;
    }

    const stale = filterCachedProducts(cart);
    if (!stale.length) return;

    document.querySelectorAll('rebuy-cart-cross-sell[hidden]').forEach((host) => {
      const sectionId = host.getAttribute('section-id') || '';
      const cfgRaw = readConfig(host);
      const d = typeof cfgRaw === 'object' && cfgRaw !== null ? cfgRaw : {};
      renderCrossSellProducts(stale, d, sectionId, host);
    });
  }

  async function waitForRecommendations(sig, timeoutMs = 12000) {
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      if (recommendationCache.key === sig && recommendationCache.products) {
        return recommendationCache.products;
      }

      if (!recommendationCache.inflight) return [];

      try {
        await recommendationCache.inflight;
      } catch (error) {
        if (error?.name === 'AbortError') continue;
        return [];
      }

      if (recommendationCache.key === sig && recommendationCache.products) {
        return recommendationCache.products;
      }

      if (!recommendationCache.inflight) return [];
    }

    return [];
  }

  function onCartUpdate(event) {
    const cart = event.detail?.cart;

    if (!merchandisingLines(cart).length) {
      clearRecommendationCache();
      return;
    }

    lastCartJson = cart;
    paintCrossSellStale(cart);

    const baseEvent = event.detail?.baseEvent;
    const sig = cartSignature(cart);

    if (baseEvent === 'gwp:reconcile') {
      if (recommendationCache.key === sig && recommendationCache.products) {
        paintCrossSellFromCache();
        return;
      }
      scheduleRecommendationFetch(cart, 32);
      return;
    }

    scheduleRecommendationFetch(cart, 0);
  }

  /** Capture: start Rebuy fetch with cart.js from event before drawer re-render. */
  document.documentElement.addEventListener('cart:change', onCartUpdate, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleRecommendationFetch(null, 0));
  } else {
    scheduleRecommendationFetch(null, 0);
  }

  /** @param {Ds & Record<string, unknown>} d */
  function buildCardMarkup(product, d, sectionId) {
    const displayVariant = pickDisplayVariant(product, d.productCardPriceStrategy ?? '');
    const atVariant = firstAvailableVariant(product);
    let imgSrc =
      typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.src || product.images?.[0]?.src || '';
    imgSrc = typeof imgSrc === 'string' ? imgSrc.trim() : '';

    try {
      if (imgSrc) imgSrc = encodeURI(imgSrc);
    } catch {
      imgSrc = '';
    }
    const link = `${Shopify.routes.root}products/${encodeURIComponent(product.handle)}`;

    const priceHtml = buildPriceMarkup(product, displayVariant || atVariant || product.variants?.[0], {
      currencyCodeEnabled: Boolean(d.currencyCodeEnabled),
      currencyIso: d.currencyIso ?? '',
      productCardPriceStrategy: d.productCardPriceStrategy ?? '',
      fromPriceTemplate: d.fromPriceTemplate ?? '',
      fromPricePlaceholder: d.fromPricePlaceholder ?? '__MIN__',
      regularPriceClasses: d.regularPriceClasses ?? '',
      onSalePriceClasses: d.onSalePriceClasses ?? d.salePriceClasses ?? '',
      salePriceClasses: d.salePriceClasses ?? '',
      comparePriceClasses: d.comparePriceClasses ?? '',
    });

    const single = Array.isArray(product.variants) && product.variants.length === 1;
    let ctaHtml = '';

    const available = product.available !== false && product.available !== 'false';

    if (single && available && atVariant) {
      const vid = String(atVariant.id);
      const action = `${Shopify.routes.root}cart/add`;
      ctaHtml = `<product-form>
        <form method="post" action="${escapeAttr(action)}" accept-charset="UTF-8" enctype="multipart/form-data">
          <input type="hidden" name="form_type" value="product"><input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="id" value="${escapeAttr(vid)}">
          <button type="submit" name="add" class="block link subheading subheading-xs">${escapeHtml(String(d.addToCart ?? ''))}</button>
        </form></product-form>`;
    } else if (available) {
      const qid = `product-quick-buy-${sectionId}-${product.id}`;
      const schemeId = escapeAttr(String(d.defaultColorSchemeId ?? 'scheme-1'));
      ctaHtml = `
        <button type="button" aria-controls="${escapeAttr(qid)}" class="link subheading subheading-xs">${escapeHtml(String(d.chooseOptions ?? ''))}</button>
        <quick-buy-modal id="${escapeAttr(qid)}" product-url="${escapeAttr(link)}" class="modal modal--quick-buy color-scheme color-scheme--${schemeId}"></quick-buy-modal>`;
    }

    const thumb = imgSrc
      ? `<a href="${escapeAttr(link)}" class="shrink-0" data-instant><img src="${escapeAttr(imgSrc)}" loading="lazy" alt="${escapeAttr(
          product.title
        )}" sizes="72px" width="144" height="144" class="constrained-image" style="--image-max-width: 72px" /></a>`
      : '';

    const snapCls = ' snap-start';
    return `<div class="product-cross-sell product-cross-sell--horizontal${snapCls}">
      <div class="h-stack gap-4 w-full">
        ${thumb}
        <div class="v-stack justify-items-start grow gap-2">
          <div class="v-stack gap-0.5">
            ${buildFragranceTitleHtml(product, link, atVariant, String(d.titleClass ?? ''))}
            ${priceHtml}
          </div>${ctaHtml}
        </div>
      </div>
    </div>`;
  }

  /** Paint from cache after drawer DOM swap — no extra Rebuy fetch. */
  function paintCrossSellFromCache() {
    if (!merchandisingLines(lastCartJson).length) return;

    const sig = cartSignature(lastCartJson);
    const products = recommendationCache.key === sig ? recommendationCache.products : null;
    if (!products?.length) return;

    document.querySelectorAll('rebuy-cart-cross-sell[hidden]').forEach((host) => {
      if (host.querySelector('.cart-drawer__cross-sell')) return;

      const sectionId = host.getAttribute('section-id') || '';
      const cfgRaw = readConfig(host);
      const d = typeof cfgRaw === 'object' && cfgRaw !== null ? cfgRaw : {};
      renderCrossSellProducts(products, d, sectionId, host);
    });
  }

  document.addEventListener('cart-drawer:refreshed', paintCrossSellFromCache);

  /**
   * @param {any[]} orderedProducts
   * @param {Ds & Record<string, unknown>} d
   * @param {string} sectionId
   * @param {HTMLElement} host
   */
  function renderCrossSellProducts(orderedProducts, d, sectionId, host) {
      const carouselId = `cart-drawer-cross-sell-rebuy-${String(sectionId).replace(/[^\w-]/g, '-')}`;
      const prevW = escapeHtml(window.themeVariables?.strings?.previous ?? '');
      const nextW = escapeHtml(window.themeVariables?.strings?.next ?? '');

      const nav =
        orderedProducts.length > 1
          ? `<div class="h-stack gap-2">
          <carousel-prev-button aria-controls="${escapeAttr(carouselId)}"><button type="button" class="circle-button" disabled>
            <span class="sr-only">${prevW}</span>
            <svg aria-hidden="true" focusable="false" width="9" part="icon" viewBox="0 0 36 36"><path fill="none" stroke="currentColor" stroke-width="2" d="M35 18H3M19.5 34.5 3 18 19.5 1.5"/></svg>
          </button></carousel-prev-button>
          <carousel-next-button aria-controls="${escapeAttr(carouselId)}"><button type="button" class="circle-button">
            <span class="sr-only">${nextW}</span>
            <svg aria-hidden="true" focusable="false" width="9" part="icon" viewBox="0 0 36 36"><path fill="none" stroke="currentColor" stroke-width="2" d="M1 18h32M16.5 1.5 33 18 16.5 34.5"/></svg>
          </button></carousel-next-button>
        </div>`
          : '';

      const crossTitleRaw = typeof d.crossSellTitle === 'string' ? d.crossSellTitle.trim() : '';
      const crossTitle = crossTitleRaw || 'You may also like';
      const titleHtml = `<p class="h7">${escapeHtml(crossTitle)}</p>`;

      const cardsHtml = orderedProducts.map((p) => buildCardMarkup(p, d, sectionId)).join('');

    host.innerHTML = `<div class="cart-drawer__cross-sell">
        <div class="h-stack justify-between gap-2">${titleHtml}${nav}</div>
        <scroll-carousel id="${escapeAttr(carouselId)}" class="cart-drawer__cross-sell-carousel scroll-area bleed">${cardsHtml}</scroll-carousel>
      </div>`;

    const surface = host.getAttribute('surface-style');

    if (surface) {
      const s = surface.trim();
      host.querySelectorAll('.product-cross-sell').forEach((node) => node.setAttribute('style', s));
    }

    hideShopifyFallback(sectionId);
    host.hidden = false;
  }

  /** @param {HTMLElement} host */
  async function mountRebuyCrossSell(host) {
    const apiKey = (host.getAttribute('api-key') || '').trim();
    const dataSourceId = (host.getAttribute('datasource-id') || '').trim();
    const sectionId = host.getAttribute('section-id') || '';
    const limit = Number(host.getAttribute('limit')) || 10;

    const cfgRaw = readConfig(host);
    const d = typeof cfgRaw === 'object' && cfgRaw !== null ? cfgRaw : {};

    if (!apiKey || !dataSourceId) {
      host.remove();
      return;
    }

    let cartJson = lastCartJson;
    if (!merchandisingLines(cartJson).length) {
      cartJson = (await loadCartJson()) || {};
      if (merchandisingLines(cartJson).length) lastCartJson = cartJson;
    }

    if (!merchandisingLines(cartJson).length) {
      host.remove();
      return;
    }

    const sig = cartSignature(cartJson);

    if (recommendationCache.key === sig && recommendationCache.products?.length) {
      hideShopifyFallback(sectionId);
      renderCrossSellProducts(recommendationCache.products, d, sectionId, host);
      return;
    }

    hideShopifyFallback(sectionId);
    paintCrossSellStale(cartJson);

    if (!recommendationCache.inflight || recommendationCache.inflightKey !== sig) {
      scheduleRecommendationFetch(cartJson, 0);
    }

    try {
      const orderedProducts = await waitForRecommendations(sig);

      if (!host.isConnected) return;

      if (!orderedProducts?.length) {
        showShopifyFallback(sectionId);
        host.remove();
        return;
      }

      renderCrossSellProducts(orderedProducts, d, sectionId, host);
    } catch (error) {
      if (!host.isConnected) return;
      console.warn('[rebuy-cart-cross-sell]', error);
      showShopifyFallback(sectionId);
      host.remove();
    }
  }

  class RebuyCartCrossSell extends HTMLElement {
    connectedCallback() {
      mountRebuyCrossSell(this);
    }
  }

  if (!window.customElements.get('rebuy-cart-cross-sell')) {
    window.customElements.define('rebuy-cart-cross-sell', RebuyCartCrossSell);
  }
})();
